import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "suggestions", "hiddenField"]
  static values = { placeholder: String }

  connect() {
    this.loadDomainsData()
    this.setupSuggestionsContainer()
    this.bindEvents()
  }

  loadDomainsData() {
    const domainsScript = document.getElementById('domains-data')
    if (domainsScript) {
      try {
        this.domainsData = JSON.parse(domainsScript.textContent)
      } catch (error) {
        console.error('Error parsing domains data:', error)
        this.domainsData = []
      }
    } else {
      this.domainsData = []
    }
  }

  setupSuggestionsContainer() {
    // Style the suggestions container
    this.suggestionsTarget.className = `
      absolute z-50 mt-2 w-full
      bg-white backdrop-blur-sm
      shadow-2xl rounded-xl border border-gray-100
      max-h-64 overflow-hidden
      transform opacity-0 scale-95 transition-all duration-200 ease-out
      hidden
    `.replace(/\s+/g, ' ')

    this.suggestionsTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'

    // Ensure input has autocomplete off
    this.inputTarget.autocomplete = 'off'
  }

  bindEvents() {
    this.inputTarget.addEventListener('input', () => this.handleInput())
    this.inputTarget.addEventListener('focus', () => this.handleFocus())
    this.inputTarget.addEventListener('keydown', (e) => this.handleKeydown(e))
    this.inputTarget.addEventListener('blur', () => {
      // Delay hiding suggestions to allow clicking on them
      setTimeout(() => this.hideSuggestions(), 150)
    })
    document.addEventListener('click', (e) => this.handleOutsideClick(e))
  }

  handleInput() {
    const query = this.inputTarget.value.trim()

    // Update hidden field for immediate search
    this.hiddenFieldTarget.value = query

    if (query.length === 0) {
      this.hideSuggestions()
      return
    }

    if (query.length >= 1) {
      this.filterAndShowSuggestions(query)
    }
  }

  handleFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 1) {
      this.filterAndShowSuggestions(query)
    }
  }

  filterAndShowSuggestions(query) {
    const lowerQuery = query.toLowerCase()

    // Filter domains that contain the substring anywhere
    const matches = this.domainsData.filter(domainData => {
      const domain = domainData.domain || domainData
      return domain.toLowerCase().includes(lowerQuery)
    })

    this.renderSuggestions(matches, query)

    if (matches.length > 0) {
      this.showSuggestions()
    } else {
      this.hideSuggestions()
    }
  }

  renderSuggestions(matches, query) {
    const scrollContainer = document.createElement('div')
    scrollContainer.className = 'max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent'

    if (matches.length === 0) {
      this.renderNoResults(scrollContainer, query)
    } else {
      this.renderMatches(scrollContainer, matches, query)
    }

    this.suggestionsTarget.innerHTML = ''
    this.suggestionsTarget.appendChild(scrollContainer)
  }

  renderNoResults(container, query) {
    const noResults = document.createElement('div')
    noResults.className = `
      cursor-default select-none relative
      px-4 py-8 mx-2 my-2 rounded-lg
      text-center text-gray-500
      bg-gradient-to-br from-gray-50 to-gray-100
      border-2 border-dashed border-gray-200
    `.replace(/\s+/g, ' ')

    noResults.innerHTML = `
      <div class="flex flex-col items-center space-y-2">
        <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
        <div class="text-sm font-medium">No domains found for "${query}"</div>
        <div class="text-xs text-gray-400">Try typing a different part of the domain</div>
      </div>
    `

    container.appendChild(noResults)
  }

  renderMatches(container, matches, query) {
    matches.forEach((domainData, index) => {
      const domain = domainData.domain || domainData
      const count = domainData.count || ''

      const suggestion = document.createElement('div')
      suggestion.className = `
        cursor-pointer select-none relative
        flex items-center justify-between
        px-4 py-3 mx-2 my-1 rounded-lg
        transition-all duration-150 ease-in-out
        hover:bg-gradient-to-r hover:from-indigo-50 hover:to-blue-50
        hover:shadow-sm hover:scale-[1.02]
        group/option
      `.replace(/\s+/g, ' ')

      suggestion.dataset.domain = domain
      suggestion.dataset.index = index

      // Create content with highlighted substring
      const contentArea = document.createElement('div')
      contentArea.className = 'flex-1'

      // Highlight the matching substring
      const highlightedDomain = this.highlightSubstring(domain, query)

      const domainSpan = document.createElement('div')
      domainSpan.className = 'font-medium text-gray-800 group-hover/option:text-indigo-600 transition-colors duration-150'
      domainSpan.innerHTML = highlightedDomain

      contentArea.appendChild(domainSpan)

      // Add count if available
      if (count) {
        const countBadge = document.createElement('div')
        countBadge.className = `
          inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
          bg-gray-100 text-gray-600 mt-1
          group-hover/option:bg-indigo-100 group-hover/option:text-indigo-700
          transition-all duration-150
        `.replace(/\s+/g, ' ')
        countBadge.textContent = `${count} sources`
        contentArea.appendChild(countBadge)
      }

      suggestion.appendChild(contentArea)

      // Add click handler
      suggestion.addEventListener('click', () => this.selectDomain(domain))

      container.appendChild(suggestion)
    })
  }

  highlightSubstring(text, query) {
    if (!query) return text

    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    const index = lowerText.indexOf(lowerQuery)

    if (index === -1) return text

    const before = text.substring(0, index)
    const match = text.substring(index, index + query.length)
    const after = text.substring(index + query.length)

    return `${before}<mark class="bg-yellow-200 text-yellow-800 rounded px-1">${match}</mark>${after}`
  }

  selectDomain(domain) {
    this.inputTarget.value = domain
    this.hiddenFieldTarget.value = domain

    // Add visual feedback
    this.inputTarget.classList.add('border-green-300', 'bg-green-50')
    setTimeout(() => {
      this.inputTarget.classList.remove('border-green-300', 'bg-green-50')
    }, 300)

    this.hideSuggestions()
    this.submitSearch()
  }

  submitSearch() {
    // Add loading state
    this.inputTarget.disabled = true
    this.inputTarget.classList.add('bg-gray-100', 'cursor-wait')

    // Submit the form
    this.inputTarget.closest('form').submit()
  }

  handleKeydown(event) {
    const suggestions = Array.from(this.suggestionsTarget.querySelectorAll('[data-domain]'))
    const currentHighlighted = this.suggestionsTarget.querySelector('.keyboard-highlight')
    let currentIndex = currentHighlighted ? suggestions.indexOf(currentHighlighted) : -1

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        currentIndex = currentIndex < suggestions.length - 1 ? currentIndex + 1 : 0
        this.highlightSuggestion(suggestions[currentIndex])
        break

      case 'ArrowUp':
        event.preventDefault()
        currentIndex = currentIndex > 0 ? currentIndex - 1 : suggestions.length - 1
        this.highlightSuggestion(suggestions[currentIndex])
        break

      case 'Enter':
        event.preventDefault()
        if (currentHighlighted) {
          const domain = currentHighlighted.dataset.domain
          this.selectDomain(domain)
        } else if (suggestions.length === 1) {
          // Auto-select if only one match
          const domain = suggestions[0].dataset.domain
          this.selectDomain(domain)
        } else {
          // Search with current input value
          this.hiddenFieldTarget.value = this.inputTarget.value.trim()
          this.hideSuggestions()
          this.submitSearch()
        }
        break

      case 'Escape':
        this.hideSuggestions()
        this.inputTarget.blur()
        break

      case 'Tab':
        this.hideSuggestions()
        break
    }
  }

  highlightSuggestion(suggestion) {
    // Remove previous highlights
    this.suggestionsTarget.querySelectorAll('[data-domain]').forEach(opt => {
      opt.classList.remove('keyboard-highlight', 'bg-indigo-200', 'ring-2', 'ring-indigo-300')
    })

    // Add highlight to current suggestion
    if (suggestion) {
      suggestion.classList.add('keyboard-highlight', 'bg-indigo-200', 'ring-2', 'ring-indigo-300')
      suggestion.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }

  showSuggestions() {
    this.suggestionsTarget.classList.remove('hidden')
    setTimeout(() => {
      this.suggestionsTarget.classList.remove('opacity-0', 'scale-95')
      this.suggestionsTarget.classList.add('opacity-100', 'scale-100')
    }, 10)
  }

  hideSuggestions() {
    this.suggestionsTarget.classList.remove('opacity-100', 'scale-100')
    this.suggestionsTarget.classList.add('opacity-0', 'scale-95')

    setTimeout(() => {
      this.suggestionsTarget.classList.add('hidden')
    }, 200)
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }
}
